/**
 * Leer la hoja de Excel que se arrastra al modo masivo.
 *
 * **No hay un segundo analizador.** El fichero se convierte a las mismas lineas
 * de texto separadas por tabuladores que produce copiar celdas en Excel, y a
 * partir de ahi entra por `analizarLista`, igual que lo pegado. Escribir aqui un
 * analizador propio duplicaria las reglas (cabecera, litraje con coma, NIF
 * repetido, extras con `+`) y las dos copias se separarian a la primera
 * correccion.
 *
 * La libreria se carga con `import()` dinamico: solo baja cuando alguien suelta
 * un fichero, asi que no engorda el paquete de quien nunca usa esta pantalla.
 */

export const EXTENSIONES = ['.xlsx', '.xlsm', '.xltx'] as const

/** Lo que Excel da por bueno y aqui no sirve. */
export class ExcelIlegible extends Error {}

const MAXIMO_BYTES = 5 * 1024 * 1024

/**
 * Una celda a texto.
 *
 * Los numeros llegan como `number`, y ahi esta el detalle que importa: un
 * litraje de 12,8 guardado en binario puede volver como 12.800000000000001, y
 * `String()` lo escribiria entero. Se redondea antes, a cuatro decimales, que
 * es mas precision de la que tiene ningun campo de un pedido.
 */
export function celdaATexto(valor: unknown): string {
  if (valor === null || valor === undefined) return ''
  if (typeof valor === 'number') {
    if (!Number.isFinite(valor)) return ''
    return String(Number(valor.toFixed(4)))
  }
  if (typeof valor === 'boolean') return valor ? 'si' : 'no'
  if (valor instanceof Date) return valor.toISOString().slice(0, 10)
  return String(valor).trim()
}

/**
 * Las filas de la hoja al texto que entiende `analizarLista`.
 *
 * Las filas vacias se caen aqui: Excel arrastra cientos de filas en blanco
 * debajo de la tabla y cada una seria una linea "sin NIF" en la revision.
 */
export function textoDeFilas(filas: readonly unknown[][]): string {
  return filas
    .map((fila) => fila.map(celdaATexto))
    .filter((fila) => fila.some((celda) => celda !== ''))
    .map((fila) => fila.join('\t'))
    .join('\n')
}

export function pareceExcel(nombre: string): boolean {
  const minusculas = nombre.toLowerCase()
  return EXTENSIONES.some((extension) => minusculas.endsWith(extension))
}

export interface HojaLeida {
  texto: string
  /** Como se llama la pestana que se ha leido. Vacio si el fichero no lo dice. */
  pestana: string
}

/**
 * La primera pestana, venga la libreria como venga.
 *
 * La compilacion para navegador de `read-excel-file` devuelve **todas** las
 * pestanas, como `[{sheet, data}, ...]`, y no las filas de la primera. Se
 * comprobo leyendo un .xlsx de verdad: con un doble en las pruebas esto no se
 * ve, y la pantalla habria llegado a produccion rota con la suite en verde.
 *
 * Se aceptan las dos formas a proposito. La documentacion de la libreria
 * describe la lista de filas, asi que una version futura puede devolver
 * cualquiera de las dos y ninguna deberia romper la pantalla.
 */
export function primeraHoja(salida: unknown): HojaLeida {
  if (!Array.isArray(salida) || salida.length === 0) return { texto: '', pestana: '' }

  const primera = salida[0]
  if (Array.isArray(primera)) {
    return { texto: textoDeFilas(salida as unknown[][]), pestana: '' }
  }
  if (primera !== null && typeof primera === 'object' && 'data' in primera) {
    const { data, sheet } = primera as { data?: unknown; sheet?: unknown }
    return {
      texto: Array.isArray(data) ? textoDeFilas(data as unknown[][]) : '',
      pestana: typeof sheet === 'string' ? sheet : '',
    }
  }
  return { texto: '', pestana: '' }
}

/**
 * El fichero, ya convertido al texto de siempre.
 *
 * `.xls` antiguo y `.csv` no entran: el primero es otro formato binario que la
 * libreria no lee, y el segundo ya se puede pegar tal cual. Decirlo aqui es
 * mejor que dejar que la libreria falle con un error suyo.
 */
export async function leerExcel(fichero: File): Promise<HojaLeida> {
  if (!pareceExcel(fichero.name)) {
    throw new ExcelIlegible(
      `"${fichero.name}" no es una hoja de Excel moderna. Guardala como .xlsx, o abre el fichero y pega las celdas en el cuadro de abajo.`,
    )
  }
  if (fichero.size > MAXIMO_BYTES) {
    throw new ExcelIlegible(
      'La hoja pesa mas de 5 MB. Un reparto son unos cientos de filas: revisa si lleva imagenes o pestanas de mas.',
    )
  }

  const { default: leerHoja } = await import('read-excel-file/browser')
  let salida: unknown
  try {
    salida = await leerHoja(fichero)
  } catch (causa) {
    throw new ExcelIlegible(
      'No se ha podido abrir la hoja. Si esta protegida con contrasena o es un .xls antiguo, guardala como .xlsx.',
      { cause: causa },
    )
  }

  const hoja = primeraHoja(salida)
  if (hoja.texto === '') {
    throw new ExcelIlegible(
      'La primera pestana del fichero esta vacia. Si los datos estan en otra, muevelos a la primera.',
    )
  }
  return hoja
}
